import { makeObservable, observable, action } from 'mobx';

/**
 * Base interface for service events
 */
export interface ServiceEvent {
  toString(): string;
}

/**
 * Base interface for service event listener
 */
export type ServiceEventListener<T = any> = (data: T) => void;

/**
 * Base interface for transcript services
 */
export interface IService<E extends ServiceEvent> {
  /** Register an event listener */
  on(event: E, listener: ServiceEventListener): EventSubscription;
  
  /** Unregister an event listener */
  off(event: E, listener: ServiceEventListener): void;
  
  /** Clean up service resources */
  cleanup(): void;
}

/**
 * Base class for transcript services
 * Provides common event handling and cleanup functionality
 */
export abstract class BaseService<E extends ServiceEvent> implements IService<E> {
  /** Map of event listeners */
  protected _listeners: Map<string, ServiceEventListener[]> = new Map();
  
  /**
   * Creates a new base service
   * @param makeObservable Whether to make the service observable
   */
  constructor(makeObservableProp: boolean = true) {
    if (makeObservableProp) {
      // Use makeObservable instead of makeAutoObservable to support inheritance
      makeObservable(this);
    }

    if ((window as any).__baseServiceInstances) {
      (window as any).__baseServiceInstances.add(this);
    } else {
      (window as any).__baseServiceInstances = new Set();
      (window as any).__baseServiceInstances.add(this);
    }
  }
  
  /**
   * Register an event listener
   * 
   * @param event Event to listen for
   * @param listener Callback function
   * @returns Subscription object for easy unsubscribing
   */
  on(event: E, listener: ServiceEventListener): EventSubscription {
    const eventKey = event.toString();
    if (!this._listeners.has(eventKey)) {
      this._listeners.set(eventKey, []);
    }
    
    this._listeners.get(eventKey)!.push(listener);
    return new EventSubscription(this, event, listener);
  }
  
  /**
   * Unregister an event listener
   * 
   * @param event Event to stop listening for
   * @param listener Callback function to remove
   */
  off(event: E, listener: ServiceEventListener): void {
    const eventKey = event.toString();
    if (!this._listeners.has(eventKey)) {
      return;
    }
    
    const listeners = this._listeners.get(eventKey)!;
    const index = listeners.indexOf(listener);
    
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  }
  
  /**
   * Notify all listeners of an event
   * 
   * @param event Event that occurred
   * @param data Data associated with the event
   */
  protected notify(event: E, data?: any): void {
    const eventKey = event.toString();
    if (!this._listeners.has(eventKey)) {
      return;
    }
    
    const listeners = this._listeners.get(eventKey)!;
    
    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        console.error(`Error in service listener for ${eventKey}:`, error);
      }
    }
  }
  
  /**
   * Clean up service resources
   * Override in derived classes to add additional cleanup logic
   */
  cleanup(): void {
    this._listeners.clear();
  }
}

/**
 * Subscription to a service event
 * Provides a convenient way to unsubscribe
 */
export class EventSubscription {
  constructor(
    private service: IService<any>,
    private event: ServiceEvent,
    private listener: ServiceEventListener
  ) {}
  
  /**
   * Unsubscribe from the event
   */
  unsubscribe(): void {
    this.service.off(this.event, this.listener);
  }
}